<script setup>
import { ref, computed, onMounted, getCurrentInstance, watch } from 'vue';
import { getMatchesByDate } from './matchDataUtils.js';
import DateCalendar from './DateCalendar.vue';
import Commons from '../../shared/libraries/commons.js';

const { proxy } = getCurrentInstance();

const props = defineProps({
  showAds: Boolean
});

const allMatchesData = ref(proxy.$stores.match.formattedMatchesByDate.value);
setTimeout(() => {
  Commons.idle(() => {
    allMatchesData.value = proxy.$stores.match.formattedMatchesByDate.value;
  });
}, 2000);
setTimeout(() => {
  Commons.idle(() => {
    allMatchesData.value = proxy.$stores.match.formattedMatchesByDate.value;
  });
}, 5000);
setInterval(() => {
  Commons.idle(() => {
    allMatchesData.value = proxy.$stores.match.formattedMatchesByDate.value;
  });
}, 10000);

const isSelectingDate = ref(true);
const activeDateId = ref('');
const isLoading = computed(
  () =>
    proxy.$stores.match.state?.list_all?.loading || !allMatchesData.value.length
);

const dates = ref(proxy.$constants.MATCH.ALL_DATES);

// cache des elements DOM pour éviter les getElementById répétés
const domCache = new Map();
const getCachedElement = (id) => {
  // si on a déjà une valeur en cache et qu'elle existe, on la retourne
  if (domCache.has(id)) {
    const cached = domCache.get(id);
    if (cached) return cached;
    // si c'est null, on retire du cache pour réessayer
    domCache.delete(id);
  }
  // pas en cache ou était null, on cherche
  const element = document.getElementById(id);
  if (element) {
    domCache.set(id, element);
  }
  return element;
};
// const allMatchesData = computed(
//   () => proxy.$stores.match.formattedMatchesByDate.value
// );

// afficher les données dès qu'elles sont disponibles, même si loading est encore true
const hasData = computed(
  () => allMatchesData.value && allMatchesData.value.length > 0
);

const showContent = computed(() => {
  // si on a des données, on les affiche tout de suite
  // sinon on attend que loading soit terminé
  return hasData.value || !isLoading.value;
});

const onDateSelectFromCalendar = (date) => {
  const today = new Date(); // Base: 10 oct 2024
  const targetDate = new Date(date);
  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  let dateId;
  if (diffDays < 0) {
    dateId = `past-${Math.abs(diffDays)}`;
  } else if (diffDays === 0) {
    dateId = 'today';
  } else {
    dateId = `future-${diffDays}`;
  }

  selectDate(dateId);
};

// Fonction pour scroller vers une date spécifique
const scrollToDate = (dateId, instantScroll) => {
  console.time(`[PERF] scrollToDate ${dateId}`);
  const element = getCachedElement(`matches-${dateId}`);
  if (element) {
    // 'auto' est vraiment instantané, contrairement à 'instant' qui n'existe pas dans l'API standard
    element.scrollIntoView({
      behavior: instantScroll ? 'auto' : 'smooth',
      block: 'start'
    });
  }
  console.timeEnd(`[PERF] scrollToDate ${dateId}`);
  return !!element;
};

// Fonction pour activer une date (quand on clique dessus)
const selectDate = (dateId, instantScroll) => {
  console.time(`[PERF] selectDate ${dateId} instant=${instantScroll}`);
  isSelectingDate.value = true;
  activeDateId.value = dateId;
  let done = scrollToDate(dateId, instantScroll);

  // en mode instant, pas besoin d'attendre - on execute tout de suite
  const executeAfterScroll = () => {
    isSelectingDate.value = false;
    centerDate(dateId, instantScroll);
    if (done) {
      updateActiveDateOnScroll();
    }
    console.timeEnd(`[PERF] selectDate ${dateId} instant=${instantScroll}`);
  };

  if (instantScroll) {
    requestAnimationFrame(executeAfterScroll);
  } else {
    setTimeout(executeAfterScroll, 1000);
  }
};

// Fonction pour centrer une date dans la barre de dates horizontale
const centerDate = (dateId, instantScroll) => {
  console.time(`[PERF] centerDate ${dateId}`);
  const dateElement = getCachedElement(dateId);
  dateElement?.scrollIntoView({
    inline: 'center',
    behavior: instantScroll ? 'auto' : 'smooth'
  });
  console.timeEnd(`[PERF] centerDate ${dateId}`);
};

// Fonction pour mettre à jour la date active en fonction du scroll
const updateActiveDateOnScroll = () => {
  if (isSelectingDate.value) return;

  console.time('[PERF] updateActiveDateOnScroll');
  const scrollMiddle = document.body.offsetHeight / 3;

  let closestSection = null;
  let minDistance = Infinity;

  // Parcourir toutes les sections de dates (avec et sans matches) pour trouver la plus proche du centre du viewport
  for (const dateData of allMatchesData.value) {
    const element = getCachedElement(`matches-${dateData.id}`);
    if (element) {
      const elementRect = element.getBoundingClientRect();

      const scrollMargin = 100; // 9rem en pixels (changé)
      const elementTopRelative = elementRect.top - scrollMargin;
      const elementMiddle = elementTopRelative + elementRect.height / 3;

      const distance = Math.abs(elementMiddle - scrollMiddle);

      if (distance < minDistance) {
        minDistance = distance;
        closestSection = dateData.id;
      }
    }
  }

  // Mettre à jour la date active et centrer dans la barre de dates
  if (closestSection && closestSection !== activeDateId.value) {
    activeDateId.value = closestSection;
    centerDate(closestSection);
  }
  console.timeEnd('[PERF] updateActiveDateOnScroll');
};

// Throttle pour optimiser les performances du scroll (augmenté à 150ms)
let scrollTimeout = null;
let scrollCallCount = 0;
const throttledScrollHandler = () => {
  scrollCallCount++;
  console.log(`[PERF] scroll event (call #${scrollCallCount})`);
  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
  }
  scrollTimeout = setTimeout(() => {
    console.log(
      `[PERF] executing throttled scroll after ${scrollCallCount} events`
    );
    scrollCallCount = 0;
    updateActiveDateOnScroll();
  }, 150);
};

// permet de forcer le scroll initial même si déjà fait
let initialScrollDone = false;
// skip le scroll initial pour affichage instantané
const skipInitialScroll = ref(true);

watch(
  () => isLoading.value,
  (newVal) => {
    if (newVal == false && !initialScrollDone) {
      initialScrollDone = true;
      console.time('[PERF] watch:isLoading false -> selectDate today');
      // affiche le contenu tout de suite, scroll après en arrière-plan
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // double rAF pour s'assurer que le rendu est fait
          if (!skipInitialScroll.value) {
            selectDate('today', true);
          }
          skipInitialScroll.value = false;
        });
      });
    }
  }
);

onMounted(() => {
  // skip le scroll initial si pas encore chargé
  if (!isLoading.value && skipInitialScroll.value) {
    skipInitialScroll.value = false;
    console.time('[PERF] onMounted -> selectDate today');
    selectDate('today', true);
    console.timeEnd('[PERF] onMounted -> selectDate today');
  }
});
</script>

<style scoped>
.shimmerElement {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>

<template>
  <div class="h-full overflow-hidden">
    <VBusHelper :emitOnMounted="$constants.EVENTS.MATCH.LIST_ALL.REFRESH" />

    <div class="bg-secondary fixed top-0 z-10 w-full">
      <div class="mobile-status-padding"></div>
      <div class="px-4 overflow-x-hidden w-full h-24">
        <div class="flex gap-4 items-center h-full">
          <div class="w-8 h-8 min-w-8 flex items-center justify-center">
            <button class="w-full h-full" v-bus:EVENTS.MATCH.SH0W_LIVE.click>
              <ColorImg
                src="/images/icons/live.svg"
                class="w-full h-full bg-white"
                alt="Live"
              />
            </button>
          </div>
          <div class="h-12 border-l border-white/20"></div>
          <div class="w-full overflow-auto flex gap-6 py-3">
            <!-- <div class="min-w-[50vw]"></div> -->
            <div
              v-for="(date, i) in dates"
              :key="i"
              :id="date.id"
              class="flex-shrink-0 cursor-pointer transition-all"
              @click="selectDate(date.id)"
            >
              <div
                class="flex flex-col justify-center items-center p-2 rounded-lg min-w-[60px]"
                :class="{
                  ' text-white scale-110': activeDateId === date.id,
                  'text-white/50': date.isPast && activeDateId !== date.id,
                  'text-white/80 ': !date.isPast && activeDateId !== date.id
                }"
              >
                <div class="font-bold uppercase">
                  {{ date.dateName }}
                </div>
                <div class="uppercase">
                  {{ date.dateDesc }}
                </div>
              </div>
            </div>
            <div class="min-w-[50vw]"></div>
          </div>
          <div class="h-12 border-l border-white/20"></div>
          <div class="w-8 h-8 min-w-8 flex items-center justify-center">
            <VDropdown
              :distance="8"
              theme="tooltip-light"
              placement="bottom-end"
              :auto-hide="true"
              :delay="{ show: 0, hide: 100 }"
            >
              <button class="w-full h-full" title="Ouvrir le calendrier">
                <ColorImg
                  src="/images/match/calendar.svg"
                  class="w-full h-full bg-white"
                  alt="Calendar"
                />
              </button>

              <template #popper="{ hide }">
                <DateCalendar
                  @select="
                    (date) => {
                      onDateSelectFromCalendar(date);
                      hide();
                    }
                  "
                />
              </template>
            </VDropdown>
          </div>
        </div>
      </div>
    </div>
    <div class="h-full overflow-hidden">
      <div class="h-full flex flex-col">
        <!-- <div class="h-[8rem]"></div> -->
        <!-- <div class="mobile-status-padding"></div> -->

        <!-- <div class="h-[30vh]" v-show="!isLoading"></div> -->
        <div class="flex-1 min-h-0">
          <!-- État de chargement avec shimmer blocks -->
          <template v-if="!showContent">
            <!-- Simuler plusieurs blocs de chargement -->
            <div
              v-for="i in 5"
              :key="`shimmer-${i}`"
              class="scroll-m-[9rem] pb-32"
            >
              <!-- Header shimmer -->
              <div class="top-0 z-[5] flex flex-col items-center mb-4">
                <div class="w-24 h-0.5 shimmerElement rounded-full mb-2"></div>
                <div class="w-48 h-6 shimmerElement rounded-lg mb-2"></div>
                <div class="w-32 h-4 shimmerElement rounded-full"></div>
                <div class="w-24 h-0.5 shimmerElement rounded-full mt-2"></div>
              </div>

              <!-- Matches shimmer blocks -->
              <div class="px-4 space-y-4">
                <!-- Simuler 2-3 cartes de match par jour -->
                <div
                  v-for="j in 2"
                  :key="`shimmer-match-${i}-${j}`"
                  class="rounded-3xl overflow-hidden"
                >
                  <div class="p-4 space-y-3">
                    <!-- Header catégorie shimmer -->
                    <div class="flex items-center gap-4">
                      <div class="w-8 h-8 shimmerElement rounded-full"></div>
                      <div class="flex-1 space-y-2">
                        <div class="w-48 h-4 shimmerElement rounded-lg"></div>
                        <div class="w-32 h-3 shimmerElement rounded-full"></div>
                      </div>
                      <div class="w-5 h-5 shimmerElement rounded-full"></div>
                    </div>

                    <!-- Matches shimmer -->
                    <div class="space-y-2 pt-2">
                      <div
                        v-for="k in 2"
                        :key="`shimmer-team-${i}-${j}-${k}`"
                        class="flex items-center gap-3"
                      >
                        <div class="w-12 h-4 shimmerElement rounded-full"></div>
                        <div class="flex-1">
                          <div
                            class="w-24 h-3 shimmerElement rounded-full mb-1"
                          ></div>
                          <div
                            class="w-16 h-3 shimmerElement rounded-full"
                          ></div>
                        </div>
                        <div class="w-8 h-4 shimmerElement rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Contenu réel (une fois chargé) -->
          <template v-else>
            <div class="h-[100vh]">
              <!-- <div class="mobile-status-padding"></div> -->

              <VirtualViewport
                :count="allMatchesData.length"
                :estimate-size="1000"
                @scroll="throttledScrollHandler"
              >
                <template #default="{ index }">
                  <div
                    v-if="allMatchesData[index]?.id"
                    :id="`matches-${allMatchesData[index]?.id}`"
                    class="scroll-m-[12rem] pb-32"
                    :class="{
                      'pt-32': index == 0
                    }"
                  >
                    <div class="mobile-status-padding"></div>
                    <div
                      class="pb-4"
                      v-if="allMatchesData[index]?.id == 'today' && showAds"
                    >
                      <AdsList></AdsList>
                    </div>

                    <!-- Header de la date -->
                    <div class="top-0 flex flex-col items-center mb-4">
                      <div class="border-b w-24 border-black mb-2"></div>
                      <div class="text-2xl font-bold">
                        {{ allMatchesData[index].dateName }},
                        {{ allMatchesData[index].dateDesc }}
                      </div>
                      <div class="opacity-90">
                        <span
                          v-if="allMatchesData[index].dayMatches.length > 0"
                        >
                          {{ allMatchesData[index].dayMatches.length }}
                          compétition{{
                            allMatchesData[index].dayMatches.length > 1
                              ? 's'
                              : ''
                          }}
                        </span>
                        <span v-else class="text-gray-500">
                          Aucun match prévu
                        </span>
                      </div>
                      <div class="border-b w-24 border-black mt-2"></div>
                    </div>

                    <!-- Contenu : matches ou message "pas de match" -->
                    <div
                      v-if="allMatchesData[index].dayMatches.length > 0"
                      class="px-4 flex flex-col gap-8"
                    >
                      <div
                        v-for="(categoryData, categoryIndex) in allMatchesData[
                          index
                        ].dayMatches"
                      >
                        <div
                          class="rounded-3xl bg-white overflow-hidden"
                          @click="
                            $bus.emit(
                              $constants.EVENTS.MATCH.SHOW_COMPETITION_DETAIL,
                              categoryData
                            )
                          "
                        >
                          <!-- Header catégorie -->
                          <div
                            class="flex bg-[#E9EEF2] items-center overflow-hidden gap-3 p-3"
                          >
                            <div class="w-8 h-8 min-w-8">
                              <ImgComponent
                                :src="categoryData.category.logo"
                                fallback="/images/match/terrain.svg"
                                class="w-full h-full"
                                alt=""
                              />
                            </div>
                            <div class="flex-1 min-w-0">
                              <div
                                class="truncate w-full text-lg font-bold text-primary-800"
                              >
                                {{ categoryData.category.name }}
                              </div>
                              <div
                                class="truncate w-full text-primary-600 text-sm"
                              >
                                {{ categoryData.category.round }}
                              </div>
                            </div>
                            <button
                              @click.stop="
                                $bus.emit($constants.EVENTS.SERVICES.START, {
                                  group: 'recharges',
                                  option: 'fund'
                                })
                              "
                              class="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-500 text-white font-bold text-sm"
                            >
                              <svg
                                class="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                ></path>
                              </svg>
                              <span>Déposer</span>
                            </button>
                          </div>

                          <!-- Matches de la catégorie -->
                          <div class="divide-y divide-gray-200">
                            <MatchesPresentation
                              v-for="(
                                match, matchIndex
                              ) in categoryData.matches"
                              :match="match"
                              @click.stop="
                                $bus.emit(
                                  $constants.EVENTS.MATCH.SHOW_MATCH_DETAIL,
                                  match
                                )
                              "
                            />
                          </div>
                        </div>

                        <div v-if="categoryIndex == 0 && index % 3 == 0">
                          <div class="py-4 bg-[#E9EEF2] rounded-xl my-6">
                            <div class="md:mb-6 flex justify-between px-4">
                              <div class="flex items-center gap-2 md:gap-3">
                                <ColorImg
                                  src="/images/navs/pronostics.svg"
                                  class="w-6 h-6 md:w-7 md:h-7 bg-current text-[#070C4C]"
                                />
                                <div
                                  class="text-[#070C4C] text-lg md:text-xl font-bold"
                                >
                                  Pronostics
                                </div>
                              </div>
                              <router-link
                                class="flex text-[#070C4C] items-center gap-2"
                                to="/pronostics"
                              >
                                <div>Voir</div>
                                <ColorImg
                                  src="/images/icons/right-arrow.svg"
                                  class="w-4 h-4 text-[#070C4C] bg-current -mt-1"
                                />
                              </router-link>
                            </div>
                            <div
                              class="w-full overflow-auto relative px-4 flex mt-3"
                            >
                              <PronosticMatchs
                                class=""
                                shortList
                              ></PronosticMatchs>
                              <div class="w-5"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Message pour les jours sans match -->
                    <div v-else class="px-4">
                      <div
                        class="flex space-y-4 flex-col"
                        v-if="allMatchesData[index]?.isLoadingMatches"
                      >
                        <div v-for="j in 2" class="rounded-3xl overflow-hidden">
                          <div class="p-4 space-y-3">
                            <!-- Header catégorie shimmer -->
                            <div class="flex items-center gap-4">
                              <div
                                class="w-8 h-8 shimmerElement rounded-full"
                              ></div>
                              <div class="flex-1 space-y-2">
                                <div
                                  class="w-48 h-4 shimmerElement rounded-lg"
                                ></div>
                                <div
                                  class="w-32 h-3 shimmerElement rounded-full"
                                ></div>
                              </div>
                              <div
                                class="w-5 h-5 shimmerElement rounded-full"
                              ></div>
                            </div>

                            <!-- Matches shimmer -->
                            <div class="space-y-2 pt-2">
                              <div
                                v-for="k in 2"
                                :key="`shimmer-team-${index}-${j}-${k}`"
                                class="flex items-center gap-3"
                              >
                                <div
                                  class="w-12 h-4 shimmerElement rounded-full"
                                ></div>
                                <div class="flex-1">
                                  <div
                                    class="w-24 h-3 shimmerElement rounded-full mb-1"
                                  ></div>
                                  <div
                                    class="w-16 h-3 shimmerElement rounded-full"
                                  ></div>
                                </div>
                                <div
                                  class="w-8 h-4 shimmerElement rounded-full"
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else class="space-y-4">
                        <div
                          class="rounded-3xl border-2 border-dashed border-gray-300 bg-gray-50/30 p-12 text-center"
                        >
                          <div
                            class="flex flex-col items-center justify-center space-y-4"
                          >
                            <!-- Icône de calendrier ou match -->
                            <div
                              class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center"
                            >
                              <svg
                                class="w-8 h-8 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                            </div>

                            <!-- Texte explicatif -->
                            <div class="space-y-2">
                              <h3 class="text-lg font-semibold text-gray-700">
                                Aucun match ce jour
                              </h3>
                              <p class="text-gray-500 text-sm max-w-xs mx-auto">
                                Les compétitions reprennent demain avec de
                                nouveaux affrontements passionnants !
                              </p>
                            </div>

                            <!-- Indicateur visuel -->
                            <div
                              class="flex items-center space-x-2 text-xs text-gray-400"
                            >
                              <svg
                                class="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <span>Journée de repos</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </VirtualViewport>
            </div>
          </template>
        </div>
        <!-- <div class="h-[28vh]" v-show="!isLoading"></div> -->
      </div>
    </div>
  </div>
</template>
